import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { TrendingUp, MapPin, DollarSign, Target, Building2, ChevronRight, Sparkles, BarChart3, ArrowRight, Star, Percent, BookmarkPlus, Bookmark, Trash2, ChevronDown, Check, Save, BellRing, Clock, MessageSquare, AlertCircle, Eye, RefreshCw, Zap, Shield, Activity } from 'lucide-react';
import Header from '../../components/ui/Header';

import { searchProperties } from '../../services/propertyService';
import { getAllCommunes } from '../../services/communeService';
import { getPropertyInquiries } from '../../services/propertyInquiryService';
import { supabase } from '../../lib/supabase';

const STORAGE_KEY = 'kapital_investor_profiles';
const LAST_PROFILE_KEY = 'kapital_investor_last_profile';

const estimateYield = (property) => {
  const price = property?.price || 0;
  if (!price) return 0;
  const avgPricePerSqm = property?.commune?.average_price || 800000;
  const surface = property?.surface_area || 80;
  const estimatedMonthlyRent = avgPricePerSqm * surface * 0.007;
  const annualRent = estimatedMonthlyRent * 12;
  return price > 0 ? ((annualRent / price) * 100) : 0;
};

const formatPrice = (price) => {
  if (!price) return '—';
  if (price >= 1_000_000_000) return `${(price / 1_000_000_000)?.toFixed(1)} Mrd FCFA`;
  if (price >= 1_000_000) return `${(price / 1_000_000)?.toFixed(0)} M FCFA`;
  return `${price?.toLocaleString('fr-FR')} FCFA`;
};

const BUDGET_PRESETS = [
  { label: '< 50M', max: 50_000_000 },
  { label: '50M – 150M', min: 50_000_000, max: 150_000_000 },
  { label: '150M – 500M', min: 150_000_000, max: 500_000_000 },
  { label: '> 500M', min: 500_000_000 },
];

const YIELD_PRESETS = [
  { label: '> 4%', min: 4 },
  { label: '> 6%', min: 6 },
  { label: '> 8%', min: 8 },
  { label: '> 10%', min: 10 },
];

const loadProfiles = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
};
const saveProfiles = (profiles) => { localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles)); };
const loadLastProfile = () => {
  try { return JSON.parse(localStorage.getItem(LAST_PROFILE_KEY) || 'null'); } catch { return null; }
};
const saveLastProfile = (profile) => { localStorage.setItem(LAST_PROFILE_KEY, JSON.stringify(profile)); };

const emptyForm = { budgetMin: '', budgetMax: '', yieldMin: '', communeId: '' };

const InvestorMode = () => {
  const [communes, setCommunes] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [activeBudgetPreset, setActiveBudgetPreset] = useState(null);
  const [activeYieldPreset, setActiveYieldPreset] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileName, setProfileName] = useState('');
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const dropdownRef = useRef(null);
  const [watchlistSummary, setWatchlistSummary] = useState({ activeProfiles: 0, alertsThisWeek: 0, loading: true });
  const [bestProperties, setBestProperties] = useState([]);
  const [bestPropertiesLoading, setBestPropertiesLoading] = useState(true);
  const [pendingInquiries, setPendingInquiries] = useState([]);
  const [pendingInquiriesLoading, setPendingInquiriesLoading] = useState(true);
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);

  const loadWatchlistSummary = async () => {
    try {
      const storedProfiles = loadProfiles();
      const activeProfiles = storedProfiles?.length || 0;
      const oneWeekAgo = new Date();
      oneWeekAgo?.setDate(oneWeekAgo?.getDate() - 7);
      const { data: alertsData } = await supabase
        ?.from('watchlist_property_alerts')
        ?.select('id, updated_at, is_active')
        ?.eq('is_active', true)
        ?.gte('updated_at', oneWeekAgo?.toISOString());
      setWatchlistSummary({ activeProfiles, alertsThisWeek: alertsData?.length || 0, loading: false });
    } catch {
      setWatchlistSummary(prev => ({ ...prev, loading: false }));
    }
  };

  const loadBestProperties = async () => {
    setBestPropertiesLoading(true);
    try {
      const { data } = await searchProperties({ limit: 50 });
      const sorted = (data || [])
        ?.map(p => ({ ...p, estimatedYield: estimateYield(p) }))
        ?.filter(p => p?.estimatedYield > 0)
        ?.sort((a, b) => b?.estimatedYield - a?.estimatedYield)
        ?.slice(0, 3);
      setBestProperties(sorted);
    } catch { setBestProperties([]); } finally { setBestPropertiesLoading(false); }
  };

  const loadPendingInquiries = async () => {
    setPendingInquiriesLoading(true);
    try {
      const { data } = await getPropertyInquiries();
      const pending = (data || [])?.filter(i => i?.status === 'new' || i?.status === 'pending')?.slice(0, 5);
      setPendingInquiries(pending);
    } catch { setPendingInquiries([]); } finally { setPendingInquiriesLoading(false); }
  };

  const refreshDashboard = async () => {
    setDashboardRefreshing(true);
    await Promise.all([loadWatchlistSummary(), loadBestProperties(), loadPendingInquiries()]);
    setDashboardRefreshing(false);
  };

  useEffect(() => {
    getAllCommunes()?.then(({ data }) => setCommunes(data || []));
    const stored = loadProfiles();
    setProfiles(stored);
    const last = loadLastProfile();
    if (last) {
      setForm(last?.form || emptyForm);
      setActiveProfileId(last?.id || null);
      setActiveBudgetPreset(last?.activeBudgetPreset ?? null);
      setActiveYieldPreset(last?.activeYieldPreset ?? null);
    }
    loadWatchlistSummary();
    loadBestProperties();
    loadPendingInquiries();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) setShowProfileDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const applyBudgetPreset = (preset, idx) => {
    setActiveBudgetPreset(idx);
    setForm(prev => ({ ...prev, budgetMin: preset?.min?.toString() || '', budgetMax: preset?.max?.toString() || '' }));
  };

  const applyYieldPreset = (preset, idx) => {
    setActiveYieldPreset(idx);
    setForm(prev => ({ ...prev, yieldMin: preset?.min?.toString() }));
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setSearched(false);
    saveLastProfile({ id: activeProfileId, form, activeBudgetPreset, activeYieldPreset });
    try {
      const filters = {};
      if (form?.budgetMin) filters.min_price = parseFloat(form?.budgetMin);
      if (form?.budgetMax) filters.max_price = parseFloat(form?.budgetMax);
      if (form?.communeId) filters.commune_id = form?.communeId;
      const { data, error: fetchError } = await searchProperties(filters);
      if (fetchError) { setError(fetchError); setResults([]); } else {
        let filtered = data || [];
        if (form?.yieldMin) {
          const minYield = parseFloat(form?.yieldMin);
          filtered = filtered?.filter(p => estimateYield(p) >= minYield);
        }
        filtered?.sort((a, b) => estimateYield(b) - estimateYield(a));
        setResults(filtered);
      }
    } catch { setError('Erreur lors de la recherche. Veuillez réessayer.'); }
    finally { setLoading(false); setSearched(true); }
  };

  const handleReset = () => {
    setForm(emptyForm);
    setActiveBudgetPreset(null);
    setActiveYieldPreset(null);
    setActiveProfileId(null);
    setResults([]);
    setSearched(false);
    setError('');
    setShowSaveInput(false);
    setProfileName('');
    localStorage.removeItem(LAST_PROFILE_KEY);
  };

  const handleSaveProfile = () => {
    if (!profileName?.trim()) return;
    const existing = profiles?.find(p => p?.name?.toLowerCase() === profileName?.trim()?.toLowerCase());
    let updated;
    if (existing) {
      updated = profiles?.map(p => p?.id === existing?.id ? { ...p, form, activeBudgetPreset, activeYieldPreset, updatedAt: Date.now() } : p);
      setActiveProfileId(existing?.id);
      saveLastProfile({ id: existing?.id, form, activeBudgetPreset, activeYieldPreset });
    } else {
      const newProfile = { id: Date.now()?.toString(), name: profileName?.trim(), form, activeBudgetPreset, activeYieldPreset, createdAt: Date.now() };
      updated = [newProfile, ...profiles];
      setActiveProfileId(newProfile?.id);
      saveLastProfile({ id: newProfile?.id, form, activeBudgetPreset, activeYieldPreset });
    }
    saveProfiles(updated);
    setProfiles(updated);
    setProfileName('');
    setShowSaveInput(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleLoadProfile = (profile) => {
    setForm(profile?.form || emptyForm);
    setActiveBudgetPreset(profile?.activeBudgetPreset ?? null);
    setActiveYieldPreset(profile?.activeYieldPreset ?? null);
    setActiveProfileId(profile?.id);
    saveLastProfile({ id: profile?.id, form: profile?.form, activeBudgetPreset: profile?.activeBudgetPreset, activeYieldPreset: profile?.activeYieldPreset });
    setShowProfileDropdown(false);
    setResults([]);
    setSearched(false);
  };

  const handleDeleteProfile = (e, profileId) => {
    e?.stopPropagation();
    let updated = profiles?.filter(p => p?.id !== profileId);
    saveProfiles(updated);
    setProfiles(updated);
    if (activeProfileId === profileId) { setActiveProfileId(null); localStorage.removeItem(LAST_PROFILE_KEY); }
  };

  const activeProfile = profiles?.find(p => p?.id === activeProfileId);

  const getProfileSummary = (profile) => {
    const parts = [];
    if (profile?.form?.budgetMax) parts?.push(`≤ ${formatPrice(parseFloat(profile?.form?.budgetMax))}`);
    else if (profile?.form?.budgetMin) parts?.push(`≥ ${formatPrice(parseFloat(profile?.form?.budgetMin))}`);
    if (profile?.form?.yieldMin) parts?.push(`${profile?.form?.yieldMin}%+ rdt`);
    return parts?.join(' · ') || 'Tous critères';
  };

  return (
    <>
      <Helmet>
        <title>Mode Investisseur — KapitalStates</title>
        <meta name="description" content="Trouvez les meilleurs investissements immobiliers selon votre budget, rendement souhaité et ville." />
      </Helmet>
      
      {/* Fond global très sombre et sans bordure */}
      <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden border-none outline-none">
        <Header />

        {/* ══ HERO ══ */}
        <div className="relative overflow-hidden border-none outline-none pt-10">
          {/* Grille de fond subtile */}
          <div className="absolute inset-0 pointer-events-none opacity-20" style={{backgroundImage:'linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px)',backgroundSize:'64px 64px'}} />
          
          {/* Halos lumineux (Glows) */}
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-brand-gold/10 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[300px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative z-10 max-w-6xl mx-auto px-6 pt-12 pb-16 flex flex-col items-center text-center">
            
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md rounded-full px-5 py-2 mb-8 border-none shadow-lg">
              <div className="w-2 h-2 bg-brand-gold rounded-full animate-pulse shadow-[0_0_8px_rgba(201,168,76,0.8)]" />
              <span className="text-white/80 text-xs font-system font-bold tracking-widest uppercase">Mode Investisseur</span>
            </div>

            <div className="max-w-4xl">
              <h1 className="text-5xl md:text-7xl font-heading font-bold text-white leading-tight tracking-tighter mb-6">
                Identifiez les <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold via-yellow-200 to-brand-gold">
                  meilleures opportunités.
                </span>
              </h1>
              <p className="text-white/60 text-lg md:text-xl font-body leading-relaxed max-w-2xl mx-auto font-light">
                Définissez vos critères d'investissement — budget, rendement cible, localisation — et obtenez instantanément les biens les plus rentables du marché ivoirien.
              </p>
            </div>

            {/* Quick stats strip */}
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              {[
                { icon: Shield, label: 'Données vérifiées', color: 'text-emerald-400' },
                { icon: Activity, label: 'Marché temps réel', color: 'text-blue-400' },
                { icon: Zap, label: 'Analyse instantanée', color: 'text-brand-gold' },
              ]?.map(({ icon: Icon, label, color }, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white/5 px-4 py-2 rounded-full backdrop-blur-sm">
                  <Icon size={16} className={color} />
                  <span className="text-white/70 text-sm font-system">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-10 space-y-10 border-none outline-none relative z-20">

          {/* ══ DASHBOARD METRICS ══ */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-white text-xl font-heading font-semibold tracking-tight">Tableau de bord</h2>
                <div className="flex items-center gap-1.5 bg-emerald-500/10 rounded-full px-3 py-1">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-emerald-400 text-[10px] font-system font-bold uppercase tracking-widest">Live</span>
                </div>
              </div>
              <button
                onClick={refreshDashboard}
                disabled={dashboardRefreshing}
                className="flex items-center gap-2 text-white/40 hover:text-white text-sm font-system transition-colors"
              >
                <RefreshCw size={14} className={dashboardRefreshing ? 'animate-spin' : ''} />
                <span>Actualiser</span>
              </button>
            </div>

            {/* Metric cards row - Glassmorphism, Zéro Bordure */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
              {[
                {
                  icon: Bookmark,
                  iconBg: 'bg-brand-gold/10',
                  iconColor: 'text-brand-gold',
                  value: watchlistSummary?.loading ? null : watchlistSummary?.activeProfiles,
                  label: 'Profils actifs',
                  sub: 'sauvegardés',
                },
                {
                  icon: BellRing,
                  iconBg: 'bg-orange-500/10',
                  iconColor: 'text-orange-400',
                  value: watchlistSummary?.loading ? null : watchlistSummary?.alertsThisWeek,
                  label: 'Alertes',
                  sub: 'cette semaine',
                },
                {
                  icon: MessageSquare,
                  iconBg: 'bg-blue-500/10',
                  iconColor: 'text-blue-400',
                  value: pendingInquiriesLoading ? null : pendingInquiries?.length,
                  label: 'Demandes',
                  sub: 'en attente',
                },
              ]?.map(({ icon: Icon, iconBg, iconColor, value, label, sub }, i) => (
                <div key={i} className="bg-[#111111] shadow-2xl rounded-3xl p-6 flex items-center gap-5 border-none outline-none">
                  <div className={`w-14 h-14 ${iconBg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <Icon size={24} className={iconColor} />
                  </div>
                  <div className="min-w-0">
                    {value === null ? (
                      <div className="h-8 w-12 bg-white/10 rounded-lg animate-pulse mb-1" />
                    ) : (
                      <p className="text-white text-3xl font-heading font-bold leading-none mb-1">{value}</p>
                    )}
                    <p className="text-white/40 text-xs font-system">{label} <span className="text-white/20">· {sub}</span></p>
                  </div>
                </div>
              ))}
            </div>

            {/* Best properties + Inquiries */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              {/* Best performers */}
              <div className="bg-[#111111] shadow-2xl rounded-3xl overflow-hidden border-none outline-none flex flex-col">
                <div className="flex items-center justify-between px-6 py-5 bg-white/[0.02]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-gold/10 rounded-xl flex items-center justify-center">
                      <TrendingUp size={16} className="text-brand-gold" />
                    </div>
                    <span className="text-white text-base font-heading font-semibold">Top rendements</span>
                  </div>
                  <Link to="/property-search-listings" className="text-brand-gold hover:text-white text-xs font-system font-medium transition-colors">
                    Tout voir
                  </Link>
                </div>

                <div className="p-4 space-y-2 flex-grow">
                  {bestPropertiesLoading ? (
                    [1,2,3]?.map(i => (
                      <div key={i} className="flex items-center gap-4 p-3 animate-pulse bg-white/5 rounded-2xl">
                        <div className="w-10 h-10 bg-white/10 rounded-xl flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-white/10 rounded w-3/4" />
                          <div className="h-2.5 bg-white/5 rounded w-1/2" />
                        </div>
                      </div>
                    ))
                  ) : bestProperties?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <Building2 size={32} className="text-white/20 mb-3" />
                      <p className="text-white/40 text-sm font-system">Aucune propriété disponible</p>
                    </div>
                  ) : (
                    bestProperties?.map((property, idx) => (
                      <Link
                        key={property?.id}
                        to={`/property/${property?.id}`}
                        className="flex items-center gap-4 p-3 rounded-2xl bg-white/[0.02] hover:bg-white/[0.06] transition-colors group border-none"
                      >
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                          idx === 0 ? 'bg-brand-gold text-black shadow-lg shadow-brand-gold/20' : 'bg-white/10 text-white/50'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/5 flex-shrink-0 relative">
                          {property?.thumbnail_url || property?.image_url ? (
                            <img src={property?.thumbnail_url || property?.image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Building2 size={16} className="text-white/20" /></div>
                          )}
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-system font-medium truncate group-hover:text-brand-gold transition-colors">
                            {property?.title || 'Propriété'}
                          </p>
                          <p className="text-white/40 text-xs font-system truncate mt-0.5">
                            {property?.commune?.name || '—'} · {formatPrice(property?.price)}
                          </p>
                        </div>
                        <div className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold ${
                          property?.estimatedYield >= 8 ? 'bg-emerald-400/10 text-emerald-400' :
                          property?.estimatedYield >= 5 ? 'bg-brand-gold/10 text-brand-gold': 'bg-orange-400/10 text-orange-400'
                        }`}>
                          ~{property?.estimatedYield?.toFixed(1)}%
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>

              {/* Pending inquiries */}
              <div className="bg-[#111111] shadow-2xl rounded-3xl overflow-hidden border-none outline-none flex flex-col">
                <div className="flex items-center justify-between px-6 py-5 bg-white/[0.02]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <Clock size={16} className="text-blue-400" />
                    </div>
                    <span className="text-white text-base font-heading font-semibold">Demandes en attente</span>
                  </div>
                  <Link to="/user-appointment-management" className="text-blue-400 hover:text-white text-xs font-system font-medium transition-colors">
                    Gérer
                  </Link>
                </div>

                <div className="p-4 space-y-2 flex-grow">
                  {pendingInquiriesLoading ? (
                    [1,2,3]?.map(i => (
                      <div key={i} className="flex items-start gap-4 p-3 animate-pulse bg-white/5 rounded-2xl">
                        <div className="w-10 h-10 bg-white/10 rounded-xl flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-white/10 rounded w-3/4" />
                          <div className="h-2.5 bg-white/5 rounded w-1/2" />
                        </div>
                      </div>
                    ))
                  ) : pendingInquiries?.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mb-3">
                        <Check size={24} className="text-emerald-400" />
                      </div>
                      <p className="text-emerald-400 text-sm font-system font-medium">Tout est à jour</p>
                      <p className="text-white/40 text-xs font-system mt-1">Aucune demande en attente</p>
                    </div>
                  ) : (
                    <>
                      {pendingInquiries?.map((inquiry) => {
                        const createdAt = inquiry?.created_at ? new Date(inquiry?.created_at) : null;
                        const hoursAgo = createdAt ? Math.floor((Date.now() - createdAt?.getTime()) / 3600000) : null;
                        const isUrgent = hoursAgo !== null && hoursAgo >= 24;
                        return (
                          <div key={inquiry?.id} className={`flex items-start gap-4 p-4 rounded-2xl transition-all border-none ${
                            isUrgent ? 'bg-red-500/10' : 'bg-white/[0.02]'
                          }`}>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isUrgent ? 'bg-red-500/20' : 'bg-blue-500/10'}`}>
                              {isUrgent ? <AlertCircle size={18} className="text-red-400" /> : <MessageSquare size={18} className="text-blue-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-system font-medium leading-snug line-clamp-2">
                                {inquiry?.message || 'Demande de renseignements'}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                {inquiry?.property?.title && (
                                  <span className="text-white/40 text-xs font-system truncate max-w-[150px]">{inquiry?.property?.title}</span>
                                )}
                                {hoursAgo !== null && (
                                  <span className={`text-xs font-bold flex-shrink-0 ${isUrgent ? 'text-red-400' : 'text-white/40'}`}>
                                    {hoursAgo < 1 ? '< 1h' : hoursAgo < 24 ? `${hoursAgo}h` : `${Math.floor(hoursAgo / 24)}j`}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ══ SEARCH PANEL ══ */}
          <div className="bg-[#111111] shadow-2xl rounded-3xl overflow-hidden border-none outline-none">

            {/* Panel header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 px-8 py-6 bg-white/[0.02]">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-brand-gold/10 rounded-xl flex items-center justify-center">
                  <Target size={20} className="text-brand-gold" />
                </div>
                <div>
                  <h2 className="text-white text-lg font-heading font-semibold">Critères de recherche</h2>
                  {activeProfile && (
                    <p className="text-brand-gold text-xs font-system mt-1 flex items-center gap-1">
                      <Check size={12} /> Profil chargé : {activeProfile?.name}
                    </p>
                  )}
                </div>
              </div>

              {/* Profile controls */}
              <div className="flex items-center gap-3">
                {profiles?.length > 0 && (
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setShowProfileDropdown(v => !v)}
                      className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white rounded-xl px-4 py-3 text-sm font-system font-medium transition-all border-none"
                    >
                      <Bookmark size={16} />
                      <span>Profils ({profiles?.length})</span>
                      <ChevronDown size={14} className={`transition-transform duration-200 ${showProfileDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showProfileDropdown && (
                      <div className="absolute right-0 top-full mt-2 w-80 bg-[#1A1A1A] rounded-2xl shadow-2xl z-50 overflow-hidden border-none outline-none">
                        <div className="p-3 space-y-1">
                          {profiles?.map(profile => (
                            <button
                              key={profile?.id}
                              onClick={() => handleLoadProfile(profile)}
                              className={`w-full flex items-center justify-between px-4 py-4 rounded-xl transition-all duration-150 group border-none ${
                                activeProfileId === profile?.id ? 'bg-brand-gold/10' : 'hover:bg-white/5'
                              }`}
                            >
                              <div className="flex-1 text-left min-w-0">
                                <p className={`text-sm font-bold truncate ${activeProfileId === profile?.id ? 'text-brand-gold' : 'text-white'}`}>
                                  {profile?.name}
                                </p>
                                <p className="text-white/40 text-xs font-system truncate mt-1">{getProfileSummary(profile)}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                {activeProfileId === profile?.id && <Check size={14} className="text-brand-gold" />}
                                <button
                                  onClick={(e) => handleDeleteProfile(e, profile?.id)}
                                  className="p-2 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-all border-none"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!showSaveInput ? (
                  <button
                    onClick={() => setShowSaveInput(true)}
                    className="flex items-center gap-2 bg-white text-black hover:bg-gray-200 rounded-xl px-4 py-3 text-sm font-bold transition-all border-none"
                  >
                    <BookmarkPlus size={16} />
                    <span>Sauvegarder</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2 bg-white/5 p-1.5 rounded-2xl border-none">
                    <input
                      type="text"
                      value={profileName}
                      onChange={e => setProfileName(e?.target?.value)}
                      onKeyDown={e => { if (e?.key === 'Enter') handleSaveProfile(); if (e?.key === 'Escape') { setShowSaveInput(false); setProfileName(''); } }}
                      placeholder="Nom du profil…"
                      autoFocus
                      className="bg-transparent px-3 py-2 text-white text-sm font-system placeholder-white/30 focus:outline-none w-40 border-none"
                    />
                    <button
                      onClick={handleSaveProfile}
                      disabled={!profileName?.trim()}
                      className="bg-brand-gold hover:bg-brand-gold/90 text-black rounded-xl px-4 py-2 text-sm font-bold transition-all disabled:opacity-40 border-none"
                    >
                      <Save size={14} />
                    </button>
                    <button onClick={() => { setShowSaveInput(false); setProfileName(''); }} className="text-white/40 hover:text-white px-2">✕</button>
                  </div>
                )}
              </div>
            </div>

            {/* Notifications */}
            {saveSuccess && (
              <div className="mx-8 mt-6 flex items-center gap-3 bg-emerald-500/10 rounded-xl px-5 py-4 border-none">
                <Check size={18} className="text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">Profil sauvegardé avec succès !</span>
              </div>
            )}
            
            {/* Form body */}
            <div className="p-8 space-y-10">

              {/* Budget */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <DollarSign size={18} className="text-brand-gold" />
                  <h3 className="text-white text-lg font-heading font-semibold">Budget d'investissement</h3>
                </div>
                
                {/* Presets - Style Bouton Apple (Pill) */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {BUDGET_PRESETS?.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => applyBudgetPreset(preset, idx)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border-none outline-none ${
                        activeBudgetPreset === idx
                          ? 'bg-brand-gold text-black shadow-lg shadow-brand-gold/20'
                          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {preset?.label}
                    </button>
                  ))}
                </div>
                
                {/* Inputs sans bordure */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="bg-white/5 rounded-2xl p-4 focus-within:bg-white/10 transition-colors">
                    <label className="block text-white/40 text-xs font-bold mb-2 uppercase tracking-widest">Budget Minimum (FCFA)</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={form?.budgetMin}
                      onChange={e => { setForm(p => ({ ...p, budgetMin: e?.target?.value })); setActiveBudgetPreset(null); }}
                      placeholder="Ex: 50 000 000"
                      className="w-full bg-transparent text-white text-xl font-bold placeholder-white/20 focus:outline-none border-none outline-none"
                    />
                  </div>
                  <div className="bg-white/5 rounded-2xl p-4 focus-within:bg-white/10 transition-colors">
                    <label className="block text-white/40 text-xs font-bold mb-2 uppercase tracking-widest">Budget Maximum (FCFA)</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={form?.budgetMax}
                      onChange={e => { setForm(p => ({ ...p, budgetMax: e?.target?.value })); setActiveBudgetPreset(null); }}
                      placeholder="Ex: 200 000 000"
                      className="w-full bg-transparent text-white text-xl font-bold placeholder-white/20 focus:outline-none border-none outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Yield */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <Percent size={18} className="text-brand-gold" />
                  <h3 className="text-white text-lg font-heading font-semibold">Rendement locatif cible</h3>
                </div>
                <div className="flex flex-wrap gap-3 mb-6">
                  {YIELD_PRESETS?.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => applyYieldPreset(preset, idx)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border-none outline-none ${
                        activeYieldPreset === idx
                          ? 'bg-brand-gold text-black shadow-lg shadow-brand-gold/20'
                          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {preset?.label}
                    </button>
                  ))}
                </div>
                <div className="bg-white/5 rounded-2xl p-4 focus-within:bg-white/10 transition-colors max-w-sm">
                  <label className="block text-white/40 text-xs font-bold mb-2 uppercase tracking-widest">Rendement Min (%)</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    value={form?.yieldMin}
                    onChange={e => { setForm(p => ({ ...p, yieldMin: e?.target?.value })); setActiveYieldPreset(null); }}
                    placeholder="Ex: 6"
                    className="w-full bg-transparent text-white text-xl font-bold placeholder-white/20 focus:outline-none border-none outline-none"
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <MapPin size={18} className="text-brand-gold" />
                  <h3 className="text-white text-lg font-heading font-semibold">Zone de recherche</h3>
                </div>
                <div className="bg-white/5 rounded-2xl p-4 focus-within:bg-white/10 transition-colors max-w-sm relative">
                   <label className="block text-white/40 text-xs font-bold mb-2 uppercase tracking-widest">Commune</label>
                   <select
                    value={form?.communeId}
                    onChange={e => setForm(p => ({ ...p, communeId: e?.target?.value }))}
                    className="w-full bg-transparent text-white text-lg font-bold focus:outline-none appearance-none border-none outline-none cursor-pointer"
                  >
                    <option value="" className="bg-[#111] text-white">Toutes les communes</option>
                    {communes?.map(c => (
                      <option key={c?.id} value={c?.id} className="bg-[#111] text-white">{c?.name}</option>
                    ))}
                  </select>
                  <div className="absolute right-6 top-1/2 mt-1 pointer-events-none">
                     <ChevronDown size={20} className="text-white/40" />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-5 pt-4">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="flex flex-1 sm:flex-none items-center justify-center gap-3 bg-brand-gold hover:bg-yellow-400 text-black px-10 py-4 rounded-full font-bold text-base transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-brand-gold/20 disabled:opacity-50 border-none outline-none"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <Sparkles size={18} />
                  )}
                  <span>{loading ? 'Analyse en cours…' : 'Lancer la recherche'}</span>
                </button>
                
                {searched && (
                  <button onClick={handleReset} className="text-white/40 text-sm font-bold hover:text-white transition-colors py-4 px-6 text-center border-none outline-none bg-white/5 hover:bg-white/10 rounded-full">
                    Réinitialiser les filtres
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ══ RESULTS ══ */}
          {searched && (
            <div className="pt-8">
              {error ? (
                <div className="bg-red-500/10 rounded-3xl p-8 text-center border-none outline-none">
                  <p className="text-red-400 font-medium">{error}</p>
                </div>
              ) : results?.length === 0 ? (
                <div className="bg-[#111111] shadow-2xl rounded-3xl p-12 text-center border-none outline-none">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Target size={32} className="text-white/20" />
                  </div>
                  <h3 className="text-white text-2xl font-heading font-bold mb-3">Aucune opportunité trouvée</h3>
                  <p className="text-white/50 text-base max-w-md mx-auto font-light">
                    Le marché actuel ne propose pas de biens correspondant exactement à ces critères. Essayez d'élargir votre budget ou de réduire vos attentes de rendement.
                  </p>
                </div>
              ) : (
                <>
                  {/* Results header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                      <h2 className="text-white text-3xl font-heading font-bold tracking-tight">
                        {results?.length} opportunité{results?.length > 1 ? 's' : ''}
                      </h2>
                      <p className="text-white/50 text-sm mt-2 font-light">Sélection classée par rentabilité décroissante</p>
                    </div>
                    <div className="flex items-center gap-2 bg-brand-gold/10 rounded-full px-5 py-2.5 self-start sm:self-auto border-none">
                      <TrendingUp size={16} className="text-brand-gold" />
                      <span className="text-brand-gold text-sm font-bold">Tri optimisé</span>
                    </div>
                  </div>

                  {/* Cards grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results?.map((property, idx) => {
                      const yieldPct = estimateYield(property);
                      const isTopPick = idx === 0;
                      return (
                        <div
                          key={property?.id}
                          className="relative bg-[#111111] shadow-xl rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-gold/10 group border-none outline-none flex flex-col"
                        >
                          {isTopPick && (
                            <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-brand-gold text-black text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                              <Star size={12} fill="currentColor" /> Meilleur choix
                            </div>
                          )}
                          
                          {/* Image */}
                          <div className="h-56 bg-white/5 relative overflow-hidden">
                            {property?.thumbnail_url || property?.image_url ? (
                              <img
                                src={property?.thumbnail_url || property?.image_url}
                                alt="Aperçu"
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Building2 size={40} className="text-white/10" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-90" />
                            
                            {/* Yield badge */}
                            <div className="absolute bottom-4 left-4">
                              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold backdrop-blur-md shadow-lg border-none ${
                                yieldPct >= 8 ? 'bg-emerald-500/90 text-white' :
                                yieldPct >= 5 ? 'bg-brand-gold/90 text-black': 'bg-orange-500/90 text-white'
                              }`}>
                                <BarChart3 size={14} />
                                ~{yieldPct?.toFixed(1)}% / an
                              </div>
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div className="p-6 flex-grow flex flex-col justify-between">
                            <div>
                              <h3 className="text-white text-lg font-heading font-bold leading-snug mb-3 line-clamp-1 group-hover:text-brand-gold transition-colors">
                                {property?.title || 'Propriété Investisseur'}
                              </h3>
                              <div className="flex items-center gap-2 mb-6">
                                <MapPin size={14} className="text-white/40 flex-shrink-0" />
                                <span className="text-white/60 text-sm font-body">
                                  {property?.commune?.name || 'Abidjan'}
                                </span>
                              </div>

                              {/* Stats row */}
                              <div className="grid grid-cols-2 gap-3 mb-6">
                                <div className="bg-white/5 rounded-2xl p-4">
                                  <p className="text-white/40 text-xs uppercase tracking-widest font-bold mb-1">Prix</p>
                                  <p className="text-brand-gold text-sm font-bold">{formatPrice(property?.price)}</p>
                                </div>
                                <div className="bg-white/5 rounded-2xl p-4">
                                  <p className="text-white/40 text-xs uppercase tracking-widest font-bold mb-1">Loyer Est.</p>
                                  <p className="text-emerald-400 text-sm font-bold">
                                    {formatPrice(Math.round((property?.commune?.average_price || 800000) * (property?.surface_area || 80) * 0.007))}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <Link
                              to={`/property/${property?.id}`}
                              className="flex items-center justify-center gap-2 w-full bg-white text-black hover:bg-gray-200 rounded-xl py-3.5 text-sm font-bold transition-all duration-200 border-none outline-none"
                            >
                              <span>Détails & Analyse</span>
                              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* ROI CTA */}
                  <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 bg-gradient-to-r from-[#111111] to-[#1A1A1A] rounded-3xl p-8 shadow-2xl border-none outline-none">
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 bg-brand-gold/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <BarChart3 size={24} className="text-brand-gold" />
                      </div>
                      <div>
                        <h4 className="text-white text-xl font-heading font-bold mb-1">Allez plus loin dans l'analyse</h4>
                        <p className="text-white/50 text-sm font-light">Utilisez notre calculatrice intelligente pour simuler vos flux de trésorerie.</p>
                      </div>
                    </div>
                    <Link to="/roi-calculator-tool" className="w-full sm:w-auto flex-shrink-0">
                      <button className="flex items-center justify-center gap-2 bg-brand-gold hover:bg-yellow-400 text-black px-6 py-3.5 rounded-full font-bold transition-all w-full sm:w-auto border-none outline-none">
                        Calculateur ROI <ChevronRight size={16} />
                      </button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══ EMPTY STATE (before search) ══ */}
          {!searched && !loading && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-12 pt-8">
              {[
                { icon: DollarSign, title: 'Budget Flexible', desc: "Des investissements de 10 millions à plusieurs milliards de FCFA.", color: 'text-brand-gold', bg: 'bg-brand-gold/10' },
                { icon: TrendingUp, title: 'Data Driven', desc: 'Rendements calculés sur la base des loyers réels du marché.', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
                { icon: MapPin, title: 'Zonage Précis', desc: 'Ciblez les communes à fort potentiel de plus-value.', color: 'text-blue-400', bg: 'bg-blue-400/10' },
              ]?.map(({ icon: Icon, title, desc, color, bg }, i) => (
                <div key={i} className="bg-[#111111] rounded-3xl p-8 hover:-translate-y-1 transition-transform border-none outline-none shadow-xl">
                  <div className={`w-12 h-12 ${bg} rounded-2xl flex items-center justify-center mb-5`}>
                    <Icon size={20} className={color} />
                  </div>
                  <h3 className="text-white text-lg font-heading font-bold mb-3">{title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed font-light">{desc}</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default InvestorMode;